import React, { useState } from "react";
import { CardComp, SelectComp2, DropDownComp } from "../../../components";
import PageWrapper from "../modules/PageWrapper";
import SearchBar from "../modules/SearchBar";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { TableCompData } from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import TablePagination from "../../../components/Table/Pagination";
import { useGetMemberTransactionHistoryQuery } from "../../../store/member/member-transaction-history";
import { MemberTransactionHistoryHeader } from "../../../utils/TableHeader";
import MemberTransactionDetailsModal from "./modals/MemberTransactionDetailsModal";
import { FiDownload } from "react-icons/fi";
import { BsChevronDown } from "react-icons/bs";
import { storage } from "../../../utils/storage";

export default function MemberTransactionHistory() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [transactionType, setTransactionType] = useState("all"); // "all", "CONTRIBUTION", "LOAN", "WITHDRAWAL", "REGISTRATION_FEE"
  const [modalPage, setModalPage] = useState("");
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);

  // Fetch transaction history with backend filtering
  const {
    data: transactionHistoryData,
    isFetching,
    isLoading,
  } = useGetMemberTransactionHistoryQuery({
    page: page,
    limit: 20,
    payment_type: transactionType,
    search: search || undefined,
  });

  const transactions = transactionHistoryData?.transactions || [];
  const pagination = transactionHistoryData?.pagination || {};

  // Transaction type options (excluding SMS_TOPUP for members)
  const transactionTypeOptions = [
    { label: "All Transactions", value: "all" },
    { label: "Contributions", value: "CONTRIBUTION" },
    { label: "Loans", value: "LOAN" },
    { label: "Withdrawals", value: "WITHDRAWAL" },
    { label: "Registration Fees", value: "REGISTRATION_FEE" },
    { label: "Fines", value: "ATTENDANCE_FINE" },
    { label: "Refunds", value: "FINE_REFUND" },
  ];

  const TableDropDown = [
    {
      name: "View Details",
      action: (data) => {
        // Open modal with transaction details
        setSelectedTransactionId(data?.id);
        setModalPage("ViewDetails");
      },
    },
  ];

  const handleDownloadReport = (format = "excel") => {
    const params = new URLSearchParams({
      guard: "member",
      format: format,
      payment_type: transactionType === "all" ? "" : transactionType,
      search: search || "",
    });

    const token = storage["cookieStorage"].get("access_token");
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const url = `${baseUrl}/members/transaction_histories/download?${params.toString()}`;

    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData?.message || "Failed to download report");
        }
        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);

        if (format === "pdf") {
          const newWindow = window.open(blobUrl, "_blank");
          if (!newWindow) {
            alert("Please allow popups to view the printable report");
          }
        } else {
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = `Transaction_Report_${new Date().toISOString().split("T")[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }

        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      })
      .catch((err) => {
        console.error("Download failed", err);
        alert("Failed to download report. Please try again.");
      });
  };

  const downloadOptions = [
    { name: "Download as CSV", action: () => handleDownloadReport("excel") },
    { name: "Download as PDF (Printable)", action: () => handleDownloadReport("pdf") },
  ];

  return (
    <>
      <style>{`
        .transaction-history-search-wrapper .input-group.search {
          margin-bottom: 0 !important;
        }
      `}</style>
      <PageWrapper title={"Transaction History"}>
        <section className="mt-6 space-y-6">
          {/* Filters Section */}
          <CardComp cardClassName="border-0 shadow-sm" data-tour="member-txn-filters">
            <div className="row g-3 p-3 align-items-end">
              <div className="col-lg-5 col-md-6">
                <div className="transaction-history-search-wrapper">
                  <SearchBar
                    placeholder="Search by reference, description, amount..."
                    value={search}
                    onSearch={(newValue) => {
                      setSearch(newValue);
                      setPage(1); // Reset to first page when searching
                    }}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <SelectComp2
                  setSelectedOption={(option) => {
                    setTransactionType(option?.value || "all");
                    setPage(1); // Reset to first page when filtering
                  }}
                  selectedOption={transactionType}
                  LabelText={"Transaction Type"}
                  LabelClassName={"fw-2"}
                  selectClassName={"w-100 py-2"}
                  arrayComp={transactionTypeOptions}
                />
              </div>
              <div className="col-lg-4 col-md-6 d-flex align-items-end justify-content-md-end">
                <DropDownComp
                  dropLabelClassName="bg-primary1 text-white px-4 py-2 rounded shadow-sm border-0 d-flex align-items-center gap-2"
                  arrayComp={downloadOptions}
                  input="name"
                  DropDownText={
                    <div className="d-flex align-items-center gap-2">
                      <FiDownload />
                      <span className="fw-bold">Download Report</span>
                      <BsChevronDown size={14} className="ms-1" />
                    </div>
                  }
                  style={{ minWidth: "220px" }}
                />
              </div>
            </div>
          </CardComp>

        {/* Transaction History Table */}
        <CardComp cardClassName="border-0 shadow-sm" data-tour="member-txn-table">
          <div className="mb-4 p-3">
            <h5 className="fw-bold mb-1 text-primary1">All Transactions</h5>
            <p className="text-muted mb-0 small">
              View your complete transaction history across all categories
            </p>
          </div>
          <TableCompData
            marginBottom={"1rem"}
            columns={MemberTransactionHistoryHeader(TableDropDown)}
            data={transactions}
            progressPending={isLoading || isFetching}
            progressComponent={<TableSkeletonLoader rows={10} columns={6} />}
            message={
              !isLoading &&
              !isFetching &&
              transactions.length === 0 && (
                <EmptyTable Message="No transactions found. Transactions will appear here as you make payments, receive loans, or perform other financial activities." />
              )
            }
            cellPadding="12px"
          />
          {!isLoading && !isFetching && transactions.length > 0 && pagination && (
            <div className="px-3 pb-3">
              <TablePagination
                marginTop={"0"}
                handlePageChange={setPage}
                {...pagination}
              />
            </div>
          )}
        </CardComp>

        {/* Transaction Details Modal */}
        {modalPage === "ViewDetails" && selectedTransactionId && (
          <MemberTransactionDetailsModal
            transactionId={selectedTransactionId}
            setModalPage={setModalPage}
          />
        )}
      </section>
    </PageWrapper>
    </>
  );
}

